import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Pencil, X, Check, Loader2, Calendar, Trash2, AlertTriangle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { toast } from 'sonner';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import WeightDrumPicker from './WeightDrumPicker';

const DAY_MS = 86_400_000;

function toDateInputValue(iso) {
  if (!iso) return '';
  try {
    // Works for both "YYYY-MM-DD" and full ISO timestamps
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // toISOString gives UTC; for a native <input type="date"> we want the
    // local day — but since start_date is stored as UTC midnight we keep it
    // consistent and use UTC parts.
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Pencil button on the "My journey" card. Opens a full modal with:
 *   - iOS-style drum picker for the TARGET weight (kg/lbs auto).
 *   - Native date picker for the END date (recomputes the weight-loss curve).
 * Backend recalculates end_date → duration server-side and syncs goal_weight.
 */
export default function EditJourneyButton({ plan, onUpdated }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { weightUnit, convertWeight, formatNumber } = useUnits();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Hidden native date input — clicking the custom button focuses it
  // and (on browsers that support it) triggers showPicker() so the
  // system date picker opens without iOS's ugly right-aligned chrome
  // pushing the formatted value off the card.
  const dateInputRef = useRef(null);

  // Persist weight internally in KG (what the picker & backend share).
  const [targetKg, setTargetKg] = useState(plan?.target_weight || 80);
  const [endDate, setEndDate] = useState(toDateInputValue(plan?.end_date));

  // Reset form when the modal opens with a (potentially updated) plan
  useEffect(() => {
    if (open) {
      setTargetKg(plan?.target_weight || 80);
      setEndDate(toDateInputValue(plan?.end_date));
    }
  }, [open, plan]);

  const close = () => { if (!saving) setOpen(false); };

  const startDate = plan?.start_date ? toDateInputValue(plan.start_date) : '';

  // Live preview: estimated weight loss + weeks + daily deficit-level hint
  const preview = useMemo(() => {
    if (!plan?.start_weight || !startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.round((end.getTime() - start.getTime()) / DAY_MS);
    if (days <= 0) return null;
    const weeks = days / 7;
    const lossKg = plan.start_weight - targetKg;
    const perWeekKg = lossKg / weeks;
    let safety = 'ok';
    if (perWeekKg > 1.0) safety = 'fast';
    else if (perWeekKg < 0.1) safety = 'slow';
    return { days, weeks: weeks.toFixed(1), lossKg, perWeekKg, safety };
  }, [plan?.start_weight, startDate, endDate, targetKg]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!targetKg || targetKg <= 0) {
      toast.error(t('editJourney.invalidTarget'));
      return;
    }
    if (!endDate) {
      toast.error(t('editJourney.invalidEndDate', 'Seleziona una data di fine'));
      return;
    }
    if (startDate && endDate <= startDate) {
      toast.error(t('editJourney.endBeforeStart', 'La data di fine deve essere dopo l\'inizio'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        target_weight: Math.round(targetKg * 10) / 10,
        end_date: endDate,
      };
      await httpClient.put(`${API_URL}/api/plans/${plan.plan_id}`, payload);
      toast.success(t('editJourney.saved'));
      // Ask the Service Worker to drop its API cache (harmless if unused).
      // We deliberately DON'T remove `localStorage.dashboard_cache` /
      // `sessionStorage.dash_cache` here — if the subsequent refetch were
      // to fail for any reason, nuking the cache would strand the user
      // on an infinite spinner. `loadDashboard()` already overwrites the
      // cache with fresh data on success.
      try {
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
        }
      } catch (_) { /* ignore */ }
      setOpen(false);
      // Call onUpdated with NO argument so Dashboard's `loadDashboard`
      // uses its default `showLoader=false` — passing `res.data` made it
      // truthy and forced a full-screen spinner, which in some race
      // conditions flashed the "no plan" screen while data was refetching.
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('editJourney.error'));
    } finally {
      setSaving(false);
    }
  };

  const startWeightDisplay = plan?.start_weight ? formatNumber(convertWeight(plan.start_weight), 1) : '—';

  const handleReset = async () => {
    setResetting(true);
    try {
      await httpClient.delete(`${API_URL}/api/plans/me/reset`);
      // Drop SW cache + every dashboard cache so the wizard isn't
      // hidden behind a stale "you already have a plan" snapshot.
      try {
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
        }
        localStorage.removeItem('dashboard_cache');
        sessionStorage.removeItem('dash_cache');
      } catch (_) { /* ignore */ }
      toast.success(t('editJourney.resetSuccess'));
      setShowResetConfirm(false);
      setOpen(false);
      // Send the user back to the wizard
      navigate('/app/nuovo-piano', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t('editJourney.resetError'));
    } finally {
      setResetting(false);
    }
  };

  if (!plan?.plan_id) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`ml-auto w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          isDark ? 'bg-white/5 hover:bg-white/10 text-white/70' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
        }`}
        aria-label={t('editJourney.edit')}
        title={t('editJourney.edit')}
        data-testid="edit-journey-btn"
      >
        <Pencil className="w-4 h-4" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
          onClick={close}
          data-testid="edit-journey-overlay"
        >
          <form
            onSubmit={submit}
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl flex flex-col ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
            style={{ maxHeight: '92vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-5 py-4 flex items-center justify-between border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('editJourney.title')}
              </div>
              <button
                type="button"
                onClick={close}
                className={isDark ? 'text-white/60 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
                data-testid="edit-journey-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 overflow-y-auto">
              <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('editJourney.startWeight')}: <strong>{startWeightDisplay} {weightUnit}</strong>
              </div>

              {/* Target weight — drum picker */}
              <div>
                <label className={`text-xs block mb-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('editJourney.targetWeight')}
                </label>
                <WeightDrumPicker
                  value={targetKg}
                  onChange={setTargetKg}
                  isDark={isDark}
                  unit={weightUnit}
                />
              </div>

              {/* End date — the native <input type="date"> is layered on top
                  of a styled visual, both invisible on iOS (ugly right-
                  aligned chrome) and always openable (tap goes straight to
                  the native picker, no JS needed). */}
              <div>
                <label className={`text-xs block mb-2 font-medium uppercase tracking-wider ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('editJourney.endDate', 'Data di fine')}
                </label>
                <div className="relative">
                  <div
                    className={`w-full h-12 rounded-xl flex items-center gap-3 px-4 pointer-events-none ${
                      isDark
                        ? 'bg-white/5 text-white border border-white/15'
                        : 'bg-gray-50 text-gray-900 border border-gray-200'
                    }`}
                  >
                    <Calendar className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
                    <span className="flex-1 truncate text-sm">
                      {endDate
                        ? new Date(endDate + 'T00:00:00Z').toLocaleDateString(
                            undefined,
                            { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' },
                          )
                        : t('editJourney.pickDate', 'Seleziona una data')}
                    </span>
                  </div>
                  <input
                    ref={dateInputRef}
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    // Transparent but fully interactive overlay — tapping
                    // anywhere on the visible row triggers the OS date picker.
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="edit-journey-end-date"
                  />
                </div>
                <p className={`text-[11px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('editJourney.endDateHint', 'La curva di dimagrimento viene ricalcolata automaticamente.')}
                </p>
              </div>

              {/* Live preview */}
              {preview && (
                <div
                  className={`rounded-xl p-3 text-xs ${
                    preview.safety === 'fast'
                      ? (isDark ? 'bg-red-500/10 border border-red-500/20 text-red-300' : 'bg-red-50 border border-red-200 text-red-700')
                      : preview.safety === 'slow'
                        ? (isDark ? 'bg-amber-500/10 border border-amber-500/20 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-800')
                        : (isDark ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-700')
                  }`}
                  data-testid="edit-journey-preview"
                >
                  <div className="flex justify-between mb-1">
                    <span className="opacity-80">{t('editJourney.expectedLoss', 'Perdita prevista')}</span>
                    <strong>{formatNumber(convertWeight(Math.max(0, preview.lossKg)), 1)} {weightUnit}</strong>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="opacity-80">{t('editJourney.durationPreview', 'Durata')}</span>
                    <strong>{preview.weeks} {t('editJourney.weeksShort', 'sett.')}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-80">{t('editJourney.weeklyRate', 'Ritmo settimanale')}</span>
                    <strong>{formatNumber(convertWeight(Math.max(0, preview.perWeekKg)), 2)} {weightUnit}/{t('editJourney.weekShort', 'sett')}</strong>
                  </div>
                  {preview.safety === 'fast' && (
                    <div className="mt-2 font-medium">
                      ⚠️ {t('editJourney.fastWarning', 'Ritmo troppo rapido — aumenta la durata')}
                    </div>
                  )}
                  {preview.safety === 'slow' && preview.lossKg > 0 && (
                    <div className="mt-2 font-medium">
                      {t('editJourney.slowWarning', 'Ritmo molto lento — puoi ridurre la durata se preferisci')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`p-4 flex items-center gap-2 border-t flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={close}
                disabled={saving}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'}`}
                data-testid="edit-journey-cancel"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 rounded-xl font-semibold bg-[#E8334A] hover:bg-[#d42a40] text-white flex items-center justify-center gap-2 shadow-lg shadow-[#E8334A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="edit-journey-submit"
              >
                {saving
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Check className="w-4 h-4" /> {t('common.save')}</>}
              </button>
            </div>

            {/* Danger zone — reset journey */}
            <div className={`px-4 pb-4 ${isDark ? 'border-t border-white/5' : 'border-t border-gray-100'} pt-3 flex-shrink-0`}>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setShowResetConfirm(true); }}
                disabled={saving || resetting}
                className={`w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
                  isDark
                    ? 'bg-red-500/10 hover:bg-red-500/15 text-red-300'
                    : 'bg-red-50 hover:bg-red-100 text-red-700'
                }`}
                data-testid="reset-journey-btn"
              >
                <Trash2 className="w-4 h-4" />
                {t('editJourney.resetButton')}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* Reset-confirm sub-modal — destructive action gated behind explicit confirmation */}
      {showResetConfirm && createPortal(
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[10000] p-4"
          onClick={() => !resetting && setShowResetConfirm(false)}
          data-testid="reset-journey-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${
              isDark ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200'
            }`}
          >
            <div className="p-6">
              <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${
                isDark ? 'bg-red-500/15 text-red-300' : 'bg-red-100 text-red-600'
              }`}>
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className={`text-lg font-semibold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('editJourney.resetConfirmTitle')}
              </h3>
              <p className={`text-sm text-center ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {t('editJourney.resetConfirmMessage')}
              </p>
            </div>
            <div className={`p-4 flex items-center gap-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                disabled={resetting}
                className={`flex-1 py-3 rounded-xl font-medium disabled:opacity-50 ${
                  isDark ? 'bg-white/5 text-white/80' : 'bg-gray-100 text-gray-700'
                }`}
                data-testid="reset-journey-cancel"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 py-3 rounded-xl font-semibold bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="reset-journey-confirm"
              >
                {resetting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Trash2 className="w-4 h-4" /> {t('editJourney.resetConfirmCta')}</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
